export const APPLICATION_FORM_RETURN_URL_PARAM = "returnUrl";

/**
 * Safe internal return path from application form query params (HR Documents edit flow).
 */
export function getApplicationFormReturnUrl(searchParams) {
  const raw = searchParams?.get?.(APPLICATION_FORM_RETURN_URL_PARAM);
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) {
      return decoded;
    }
  } catch {
    // ignore malformed encoding
  }
  return null;
}

/**
 * Where HR Documents should return after closing a form opened from that module.
 * Prefers returnUrl, then the employee id and tab already on the query string.
 */
export function resolveHrDocumentReturnPath(
  searchParams,
  fallbackTab = "application-forms",
) {
  const returnUrl = getApplicationFormReturnUrl(searchParams);
  if (returnUrl) return returnUrl;

  const userId = searchParams?.get?.("userId");
  if (!userId || !/^\d+$/.test(String(userId))) return null;

  const tab = searchParams.get("tab") || fallbackTab;
  return `/documents/${userId}?tab=${encodeURIComponent(tab)}`;
}

/** Strip deep-link params after closing an application form overlay. */
export function clearApplicationFormQueryParams(searchParams) {
  const next = new URLSearchParams(searchParams);
  next.delete("formId");
  next.delete("actualFromId");
  next.delete(APPLICATION_FORM_RETURN_URL_PARAM);
  next.delete("userId");
  return next;
}

/**
 * Resolve where to go after save, send-to-office, or close (X).
 * Priority: returnUrl (HR Documents) → onClose (Applications overlay) → legacy documents fallback.
 * Always prefers onClose when provided so role wrappers that forgot to forward it still work
 * if the parent CloseButton is used; when onClose is missing, clear deep-link params in-place.
 */
export function resolveApplicationFormClose({
  navigate,
  searchParams,
  capturedUserId,
  onClose,
  setSearchParams,
}) {
  const returnUrl = getApplicationFormReturnUrl(searchParams);
  if (returnUrl) {
    navigate(returnUrl);
    return;
  }
  if (typeof onClose === "function") {
    onClose();
    return;
  }
  if (typeof setSearchParams === "function") {
    setSearchParams(
      (prev) => clearApplicationFormQueryParams(prev),
      { replace: true },
    );
    return;
  }
  if (capturedUserId) {
    const tab = searchParams.get("tab") || "application-forms";
    navigate(`/documents/${capturedUserId}?tab=${tab}`);
    return;
  }
  navigate(window.location.pathname, { replace: true });
}
